import { Bubbles as BubblesVisualization } from '@mentimeter/visualizations';
import { Emoji } from '@mentimeter/quiz-emojis';
import * as React from 'react';
import type { Player } from '@mentimeter/quiz-types';

const LAYOUT = {
  width: 960,
  height: 540,
};
const BUBBLES_SELECTOR = 'bubbles';
const SHRINK_CIRCLES_ON_PERCENT = 0.4;
const SHRINK_CIRCLES_BY_PERCENT = 0.9;
const MIN_CIRCLE_RADIUS = 15;

interface Node {
  identifier: string;
  index: number;
  emojiShortname: string;
  color: string;
  radius: number;
  vx?: number;
  vy?: number;
  x: number;
  y: number;
}
interface PropsT {
  colors: Array<string>;
  textColor: string;
  maxNodes: number;
  players: Player[];
}

export class Bubbles extends React.PureComponent<PropsT> {
  area = 0;
  maxNodes = 75;
  emoji = new Emoji();
  nodes: Node[] = [];
  bubbles: BubblesVisualization | undefined;

  override componentDidMount() {
    this.bubbles = new BubblesVisualization({
      nodes: this.nodes,
      selector: `#${BUBBLES_SELECTOR}`,
      emoji: this.emoji,
      layout: LAYOUT,
    });
    const { players, maxNodes } = this.props;
    const { width, height } = LAYOUT;

    this.maxNodes = maxNodes;
    this.area = width * height;
    this.bubbles.updateForce();
    players.forEach(this.addPlayer);
  }

  override componentDidUpdate() {
    const { players } = this.props;

    // Add new players
    players.forEach((player) => {
      if (!this.nodes.some((node) => node.identifier === player.identifier)) {
        this.addPlayer(player);
      }
    });

    // Remove nodes that are not in players list
    this.nodes.forEach((node) => {
      if (!players.some((player) => player.identifier === node.identifier)) {
        this.removeNode(node);
      }
    });
  }

  getCirclesArea() {
    let circleArea = 0;
    this.nodes.forEach((d) => {
      circleArea += 2 * d.radius * d.radius;
    });
    return circleArea;
  }

  addPlayer = (player: Player) => {
    const { colors, textColor } = this.props;
    const { width, height } = LAYOUT;

    // Do not add more than max number of avatars
    if (this.nodes.length >= this.maxNodes) {
      return;
    }

    if (this.nodes.find((n) => n.identifier === player.identifier)) {
      return;
    }

    const coordinates = this.getSideCoordinate(width, height);
    const node = {
      emojiShortname: player.emojiShortname,
      identifier: player.identifier,
      index: this.nodes.length,
      color: colors[player.index % colors.length] ?? textColor,
      radius: this.generateRadius(Math.max(width, height) * 0.05),
      x: coordinates.x,
      y: coordinates.y,
    };

    this.nodes.push(node);
    if (this.getCirclesArea() / this.area > SHRINK_CIRCLES_ON_PERCENT) {
      this.shrinkCircles();
    }

    if (this.bubbles) {
      this.bubbles.setNodes(this.nodes);
      this.bubbles.update();
    }
  };

  removeNode(node: Node) {
    this.nodes.splice(node.index, 1);
    if (this.bubbles) {
      this.bubbles.setNodes(this.nodes);
      this.bubbles.update();
    }
  }

  shrinkCircles() {
    this.nodes.forEach((d) => {
      d.radius *= SHRINK_CIRCLES_BY_PERCENT;
      if (d.radius < MIN_CIRCLE_RADIUS) {
        d.radius = MIN_CIRCLE_RADIUS;
      }
    });
  }

  getRandomInt(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  getSideCoordinate(xMax: number, yMax: number): { x: number; y: number } {
    const randomInt = this.getRandomInt(0, 3);
    const left = randomInt === 0;
    const up = randomInt === 1;
    const right = randomInt === 2;
    const d = { x: 0, y: 0 };
    if (left) {
      d.x = -0;
      d.y = this.getRandomInt(0, yMax);
    } else if (up) {
      d.x = this.getRandomInt(0, xMax);
      d.y = -0;
    } else if (right) {
      d.x = xMax + 0;
      d.y = this.getRandomInt(0, yMax);
    } else {
      d.x = this.getRandomInt(0, xMax);
      d.y = yMax + 0;
    }
    return d;
  }

  generateRadius(defaultRadius: number) {
    return (
      defaultRadius +
      this.getRandomInt(-defaultRadius * 0.5, defaultRadius * 0.5)
    );
  }

  override render() {
    return (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 960 540"
        preserveAspectRatio="xMidYMid"
        style={{ overflow: 'visible' }}
      >
        <g id={BUBBLES_SELECTOR} />
      </svg>
    );
  }
}
